import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { SLOW } from "./queries";

/**
 * SEO 경쟁 분석 (docs/meta-crew-seo-competition-design.md, 서버 apps/api/src/biz/seo/seo-competition.controller.ts). 직원 전용.
 * 키는 ["seo", ...] 로 시작한다 → 쓰기 뒤 SEO 무효화가 함께 적용된다.
 */

export type CompetitionSource = {
  source: string;
  kind: string;
  name: string;
  home: string | null;
  topic: string | null;
  ours: boolean;
  keywords: number;
  keywordList: string[];
  appearances: number;
  top3: number;
  avgRank: number | null;
  bestRank: number;
  lastDay: string;
};
export type CompetitionSources = { days: number; top: number; area: string; keywords: number; collectedDays: number; items: CompetitionSource[] };

export type PageMetrics = { chars: number; paragraphs: number; images: number; videos: number; tables: number; links: number; maps: number; headings: number; quotes: number; tags: string[]; comments: number | null; editor: "se" | "html" };
export type PageStatus = "pending" | "ok" | "failed" | "blocked" | "unsupported" | "gone";
export type CompetitionItem = {
  rank: number;
  title: string;
  link: string;
  source: string;
  name: string;
  home: string | null;
  date: string;
  desc: string | null;
  our: boolean;
  page: {
    status: PageStatus;
    error: string | null;
    fetchedAt: string | null;
    title: string | null;
    publishedAt: string | null;
    ageDays: number | null;
    topic: string | null;
    metrics: PageMetrics | null;
    keyword: { inTitle: boolean; inFirst: boolean; count: number; per1k: number } | null;
  } | null;
};
export type Benchmark = {
  key: string;
  label: string;
  unit: string;
  ratio: boolean;
  top3: number | null;
  top10: number | null;
  samples: number;
  values: { rank: number; value: number | null }[];
  ours: number | null;
  diff: number | null;
  direction: "higher" | "lower" | null;
};
export type Change = { rank: number | null; title: string; link: string; name: string; our: boolean; previousRank: number | null };
export type CompetitionView = {
  keyword: string;
  area: string;
  collectedAt: string | null;
  total: number | null;
  depth: number | null;
  ourBestRank?: number | null;
  items: CompetitionItem[];
  benchmark: Benchmark[];
  pending: number;
  history?: { labels: string[]; series: { source: string; name: string; our: boolean; values: (number | null)[] }[] };
  changes?: { previousAt: string; entered: Change[]; left: Change[] } | null;
};
export type ProbeView = CompetitionView & { id: string; areas: string[]; error: string | null; createdAt: string };
export type ProbeRow = { id: string; keyword: string; areas: string[]; createdAt: string; error: string | null; createdByName: string | null; totals: Record<string, number> | null };

export const pageStatus: Record<PageStatus, [string, string]> = {
  pending: ["받는 중", "blue"],
  ok: ["분석됨", "teal"],
  failed: ["실패", "coral"],
  blocked: ["수집 금지", "neutral"],
  unsupported: ["로그인 필요", "neutral"],
  gone: ["없음·비공개", "neutral"],
};

export const useCompetitionSources = (customerId: string, days: number, area: string) =>
  useQuery({
    queryKey: ["seo", "competition", customerId, "sources", days, area],
    queryFn: () => api.get<CompetitionSources>(`/admin/seo/competition/customers/${customerId}/sources?days=${days}&area=${area}`),
    enabled: !!customerId,
    refetchInterval: SLOW,
  });

export const useCompetitionKeyword = (customerId: string, keywordId: string, area: string, days: number) =>
  useQuery({
    queryKey: ["seo", "competition", customerId, "keyword", keywordId, area, days],
    queryFn: () => api.get<CompetitionView>(`/admin/seo/competition/customers/${customerId}/keywords/${keywordId}?area=${area}&days=${days}`),
    enabled: !!customerId && !!keywordId,
    refetchInterval: SLOW,
  });

export const useProbes = () => useQuery({ queryKey: ["seo", "probes"], queryFn: () => api.page<ProbeRow>("/admin/seo/probes"), refetchInterval: SLOW });

/** 글을 받는 동안(pending)에는 3초마다 다시 읽는다. */
export const useProbe = (id: string, area: string) =>
  useQuery({
    queryKey: ["seo", "probe", id, area],
    queryFn: () => api.get<ProbeView>(`/admin/seo/probes/${id}?area=${area}`),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.pending ? 3_000 : false),
  });
